'use client';
import { useCurrencyContext } from '@contexts/currency-context';
import { useBuildPackagesStore } from '@store/build-package-store';
import { getHotelNightlyPrice } from '../../hotel-presentation';
import { useBuildPackageBundle } from '@hooks/use-build-package-bundle';
import {
  Button,
  Checkbox,
  Input,
  SearchMD,
  Star01,
  ToggleGroup,
  ToggleGroupItem,
  Typography,
  typographyVariants,
} from '@v2/ui';
import { DualRangeSlider, clamp, parseCurrencyInput } from '@v2/ui/Slider';
import { SliderFilter } from '@v2/ui/SliderFilter/SliderFilter';
import { cn } from '@v2/utils';
import {
  HOTEL_DISTANCE_SLIDER_MAX_MILES,
  hotelDistanceLabel,
  toHotelDistanceMiles,
  toHotelDistanceSliderValue,
} from '../../hotel-distance';
import {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import { useGetHotels } from '../../hooks/use-hotels-list';
import { HOTEL_FEATURES_LIST, STAR_RATINGS_LIST } from '../data';

function FieldWrapper(props: {
  label: string;
  hideLabel?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const { label, hideLabel, className, children } = props;
  return (
    <div className={cn('flex flex-col gap-y-3', className)}>
      {hideLabel ? null : (
        <Typography size="tmd" className="font-bold">
          {label}
        </Typography>
      )}

      {children}
    </div>
  );
}
function RatingField(props: {
  value: string[];
  onValueChange: (val: string[]) => void;
  hideLabel?: boolean;
  className?: string;
}) {
  const { value, onValueChange, hideLabel } = props;
  const minimum = value.length ? Math.min(...value.map(Number)) : 0;
  return (
    <FieldWrapper label="Filter by star rating" hideLabel={hideLabel}>
      {hideLabel ? null : (
        <p className="text-sm font-medium text-gray-700">
          Select the minimum star rating
        </p>
      )}
      {[0, 5, 4, 3, 2, 1].map((rating) => (
        <label
          key={rating}
          className="flex min-h-12 cursor-pointer items-center gap-3 py-2 text-sm font-semibold text-gray-800"
        >
          <Checkbox
            size="md"
            checked={minimum === rating}
            onCheckedChange={() =>
              onValueChange(
                minimum === rating || rating === 0 ? [] : [String(rating)]
              )
            }
          />
          {rating > 0 && (
            <span aria-hidden="true" className="flex gap-0.5">
              {STAR_RATINGS_LIST.map((star) => (
                <Star01
                  key={star}
                  size="20"
                  className={
                    star <= rating ? 'text-primary-500' : 'text-gray-200'
                  }
                  fill="currentColor"
                  pathProps={{ stroke: 'currentColor' }}
                />
              ))}
            </span>
          )}
          {rating === 0
            ? 'Any'
            : rating === 5
              ? '5 Stars'
              : `${rating} ${rating === 1 ? 'Star' : 'Stars'} & up`}
        </label>
      ))}
    </FieldWrapper>
  );
}

function HotelFeatureField(props: {
  value: string[];
  onValueChange: (val: string[]) => void;
}) {
  const { value, onValueChange } = props;
  return (
    <FieldWrapper label="Hotel feature">
      <ToggleGroup
        type="multiple"
        variant="primary"
        className="flex-wrap justify-start gap-2"
        size="sm"
        value={value}
        onValueChange={onValueChange}
      >
        {HOTEL_FEATURES_LIST.map(({ icon: Icon, label, value }) => (
          <ToggleGroupItem
            value={value}
            key={value}
            className={'gap-1 rounded-lg px-[12px] text-current'}
          >
            {Icon}
            <span
              className={cn(
                typographyVariants({
                  size: 'tmd',
                }),
                'font-medium text-current'
              )}
            >
              {label}
            </span>{' '}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </FieldWrapper>
  );
}

function toDisplayRange(
  value: number[],
  conversion: number,
  maximum: number
): [number, number] {
  return [
    Math.floor(value[0] * conversion),
    Math.min(maximum, Math.ceil(value[1] * conversion)),
  ];
}

function PriceBoundInput({
  label,
  value,
  min,
  max,
  onLiveChange,
  onCommit,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onLiveChange: (value: number) => void;
  onCommit: (value: number) => void;
}) {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(String(value));

  useEffect(() => {
    if (!focused) setText(String(value));
  }, [focused, value]);

  return (
    <label className="rounded-lg border border-gray-200 px-3 py-2.5 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="mt-1 flex items-center gap-2 font-semibold">
        <span aria-hidden="true">$</span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          aria-label={label}
          value={focused ? text : String(value)}
          onFocus={() => {
            setFocused(true);
            setText(String(value));
          }}
          onChange={(event) => {
            const next = event.target.value.replace(/[^\d]/g, '');
            setText(next);
            const parsed = parseCurrencyInput(next);
            if (parsed !== null) onLiveChange(parsed);
          }}
          onBlur={() => {
            setFocused(false);
            const parsed = parseCurrencyInput(text);
            const next = parsed === null ? value : clamp(parsed, min, max);
            setText(String(next));
            onCommit(next);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.currentTarget.blur();
          }}
          className="w-full min-w-0 bg-transparent outline-none"
        />
      </span>
    </label>
  );
}

export type PriceRangeActions = {
  apply: () => void;
  reset: () => void;
};

function PriceTravelerRangeField(props: {
  staged?: boolean;
  hideActions?: boolean;
  hideLabel?: boolean;
  actionRef?: Ref<PriceRangeActions>;
  value: number[];
  onValueChange: (val: number[]) => void;
}) {
  const { value, onValueChange, actionRef, hideActions, hideLabel } = props;
  const { maxHotelPrice } = useGetHotels();
  const { packageGuests, isBundle } = useBuildPackageBundle();
  const { currency, priceRate } = useCurrencyContext();
  const startDate = useBuildPackagesStore((state) => state.hotelStartDate);
  const endDate = useBuildPackagesStore((state) => state.hotelEndDate);
  const nightlyFactor = getHotelNightlyPrice({
    price: 1,
    lowestPrice: 0,
    isBundle: false,
    guests: packageGuests,
    startDate,
    endDate,
  });
  const conversion =
    (nightlyFactor ?? 1) *
    priceRate *
    (currency.toUpperCase() === 'CAD' ? 1.01 : 1);
  const maximum = Math.max(1, Math.ceil(maxHotelPrice * conversion));
  const [draft, setDraft] = useState(() =>
    toDisplayRange(value, conversion, maximum)
  );
  const editingRef = useRef(false);
  useEffect(() => {
    if (editingRef.current) return;
    setDraft(toDisplayRange(value, conversion, maximum));
  }, [value[0], value[1], conversion, maximum]);
  const commit = ([min, max]: number[]) => {
    const nextMin = clamp(min, 0, maximum);
    const nextMax = clamp(max, nextMin, maximum);
    onValueChange([
      nextMin / conversion,
      nextMax === maximum ? maxHotelPrice : nextMax / conversion,
    ]);
  };
  const updateDraft = (range: [number, number], commitNow = false) => {
    setDraft(range);
    if (commitNow && !props.staged) commit(range);
  };
  const unusedActionRef = useRef<PriceRangeActions>(null);
  useImperativeHandle(actionRef ?? unusedActionRef, () => ({
    apply: () => commit(draft),
    reset: () => {
      editingRef.current = false;
      setDraft([0, maximum]);
    },
  }));
  const [minimum, maximumPrice] = draft;
  const low = clamp(Math.min(minimum, maximumPrice), 0, maximum);
  const high = clamp(Math.max(minimum, maximumPrice), 0, maximum);
  const sliderValue: [number, number] =
    low < high ? [low, high] : [Math.max(0, high - 1), Math.max(high, 1)];
  const priceLabel = (price: number) =>
    `$${price}${price === maximum ? '+' : ''}`;
  return (
    <FieldWrapper
      hideLabel={hideLabel}
      label={`Price range (${currency.toUpperCase()}, ${nightlyFactor === null ? 'total' : 'per person, per night'})`}
    >
      {isBundle && (
        <p className="text-xs text-gray-500">Includes hotel and tickets.</p>
      )}
      <div className="px-2 pb-2 pt-16">
        <DualRangeSlider
          min={0}
          max={maximum}
          step={1}
          minGap={1}
          value={sliderValue}
          minThumbAriaLabel="Minimum price"
          maxThumbAriaLabel="Maximum price"
          formatAriaValue={priceLabel}
          rangeClassName="bg-primary-500"
          thumbClassName="size-6 border-white bg-primary-500 shadow-sm"
          onValueChange={(range) => {
            editingRef.current = true;
            updateDraft(range);
          }}
          onValueCommit={(range) => {
            editingRef.current = false;
            updateDraft(range, true);
          }}
          renderThumb={({ value: thumbValue }) => (
            <span
              aria-hidden="true"
              className="absolute bottom-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg border border-gray-100 bg-white px-3 py-2 text-sm font-bold text-primary-600 shadow-md after:absolute after:-bottom-1.5 after:left-1/2 after:size-3 after:-translate-x-1/2 after:rotate-45 after:bg-white"
            >
              {priceLabel(thumbValue)}
            </span>
          )}
        />
        <div className="mt-5 flex justify-between text-sm font-medium text-gray-700">
          <span>$0</span>
          <span>{priceLabel(maximum)}</span>
        </div>
      </div>
      <p className="mt-2 text-sm font-medium text-gray-700">
        Or enter minimum or maximum
      </p>
      <div className="grid grid-cols-2 gap-3">
        <PriceBoundInput
          label="Minimum price"
          value={minimum}
          min={0}
          max={maximumPrice}
          onLiveChange={(amount) => {
            editingRef.current = true;
            updateDraft([amount, maximumPrice]);
          }}
          onCommit={(amount) => {
            editingRef.current = false;
            updateDraft([amount, maximumPrice], true);
          }}
        />
        <PriceBoundInput
          label="Maximum price"
          value={maximumPrice}
          min={minimum}
          max={maximum}
          onLiveChange={(amount) => {
            editingRef.current = true;
            updateDraft([minimum, amount]);
          }}
          onCommit={(amount) => {
            editingRef.current = false;
            updateDraft([minimum, amount], true);
          }}
        />
      </div>
      {props.staged && !hideActions && (
        <div className="mt-3 flex items-center justify-between">
          <Button
            type="button"
            variant="link"
            onClick={() => {
              editingRef.current = false;
              setDraft([0, maximum]);
            }}
          >
            Reset
          </Button>
          <Button type="button" onClick={() => commit(draft)}>
            Apply
          </Button>
        </div>
      )}
    </FieldWrapper>
  );
}

function DistanceToVenueField(props: {
  value: number[];
  onValueChange: (val: number[]) => void;
  hideLabel?: boolean;
  className?: string;
}) {
  const miles = toHotelDistanceMiles(props.value);
  const sliderValue = toHotelDistanceSliderValue(props.value);
  return (
    <FieldWrapper
      label="Distance to search location"
      hideLabel={props.hideLabel}
      className={props.className}
    >
      {props.hideLabel ? null : (
        <p className="text-sm font-medium text-gray-700">
          Show hotels within this distance of your search location
        </p>
      )}
      <SliderFilter
        showThumbs="single"
        min={0}
        max={HOTEL_DISTANCE_SLIDER_MAX_MILES}
        value={sliderValue}
        onValueChange={(next) =>
          props.onValueChange(toHotelDistanceSliderValue(next))
        }
        unit=" mi"
        formatValue={(value) => `${value} mi`}
      />
      <p className="text-sm font-semibold text-gray-900">
        {hotelDistanceLabel(miles)}
      </p>
    </FieldWrapper>
  );
}

function QueryField(props: {
  value?: string;
  onValueChange: (val: string) => void;
  label?: string;
}) {
  const { value, onValueChange, label = 'Search by property name' } = props;

  const [currentVal, setCurentValue] = useState(value);

  useEffect(() => {
    if (currentVal === value) return;

    setCurentValue(value);
  }, [value]);
  return (
    <FieldWrapper label={label}>
      <Input
        value={currentVal}
        extent={'lg'}
        placeholder="E.g. Mariott"
        className="focus:border-gray-800"
        startIcon={<SearchMD pathProps={{ stroke: '#667085' }} />}
        onChange={(e) => {
          const value = e.target.value;
          onValueChange(value);
          setCurentValue(value);
        }}
      />
    </FieldWrapper>
  );
}
export {
  DistanceToVenueField,
  HotelFeatureField,
  PriceTravelerRangeField,
  QueryField,
  RatingField,
};
